/**
 * Tisk obsahu elementu ve vlastním okně s tiskovým layoutem
 * (bez Vuetify / layoutu stránky).
 *
 * @param {string} elementId
 * @param {{ title?: string, headerLines?: string[] }} [options]
 */
export function printElementById(elementId, options = {}) {
  const el = document.getElementById(elementId);
  if (!el) {
    console.warn(`Element s ID "${elementId}" nebyl nalezen.`);
    return;
  }

  const title = options.title || 'Tisk';
  const headerLines = Array.isArray(options.headerLines)
    ? options.headerLines.map((x) => (x == null ? '' : String(x).trim())).filter(Boolean)
    : [];
  const isResults = elementId === 'resultsContent';
  const modeClass = isResults ? 'print-results' : 'print-startlist';

  const clone = el.cloneNode(true);

  // Navigační tlačítka kategorií a další UI pryč
  clone.querySelectorAll(
    [
      'button',
      '.v-btn',
      '.v-pagination',
      '.v-data-table-footer',
      '.v-data-table__bottom',
      '[class*="v-data-table-footer"]',
      '.v-skeleton-loader',
    ].join(',')
  ).forEach((node) => node.remove());

  // Prázdné řádky po odstranění navigace kategorií
  clone.querySelectorAll('.v-row, .v-col, [class*="v-col-"]').forEach((node) => {
    if (!node.textContent || !node.textContent.trim()) {
      node.remove();
    }
  });

  // Odkazy → čistý text (bez URL)
  clone.querySelectorAll('a').forEach((a) => {
    const span = document.createElement('span');
    span.textContent = a.textContent || '';
    a.replaceWith(span);
  });

  // Dark theme třídy pryč — tisk je vždy světlý
  clone.querySelectorAll('*').forEach((node) => {
    if (!node.classList) return;
    [...node.classList].forEach((cls) => {
      if (cls.includes('theme--dark') || cls.includes('v-theme--dark') || cls === 'dark') {
        node.classList.remove(cls);
      }
    });
  });

  clone.querySelectorAll('[style]').forEach((node) => {
    node.style.maxWidth = '';
    node.style.minWidth = '';
    node.style.whiteSpace = 'normal';
    node.style.flexWrap = 'wrap';
    node.style.background = '';
    node.style.backgroundColor = '';
    node.style.color = '';
  });

  if (isResults) {
    removeTableColumnsByHeader(clone, ['body', 'points']);
    layoutResultsCategoryBars(clone);
    markResultsColumns(clone);
    formatResultSplitsStacked(clone);
  } else {
    convertTimeStartlistToTable(clone);
  }

  const headerHtml = headerLines.length
    ? `<header class="print-header">${headerLines.map((l) => `<div>${escapeHtml(l)}</div>`).join('')}</header>`
    : '';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="color-scheme" content="light only" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page {
      size: A4;
      margin: 8mm 10mm;
    }

    * {
      box-sizing: border-box;
      color-scheme: light only;
    }

    html, body {
      margin: 0;
      padding: 0;
      background: #fff !important;
      color: #111 !important;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 8pt;
      line-height: 1.2;
      color-scheme: light only;
      forced-color-adjust: none;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body, body * {
      box-shadow: none !important;
    }

    .print-header {
      margin: 0 0 6pt;
      padding-bottom: 4pt;
      border-bottom: 1.5px solid #333;
    }

    .print-header div:first-child {
      font-size: 11pt;
      font-weight: 700;
      color: #111;
      margin-bottom: 1pt;
    }

    .print-header div:not(:first-child) {
      font-size: 8pt;
      color: #444;
    }

    /* Vuetify / layout reset pro tisk */
    .v-row,
    .v-col,
    [class*="v-col-"],
    .v-container,
    .v-card,
    .v-card-text,
    .v-window,
    .v-window-item,
    .v-table,
    .v-table__wrapper,
    .v-data-table,
    .v-data-table__wrapper,
    .v-sheet {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      float: none !important;
      flex: none !important;
      box-shadow: none !important;
      border: none !important;
      background: transparent !important;
      overflow: visible !important;
    }

    .v-spacer,
    .v-btn,
    button,
    .v-icon,
    .v-progress-linear,
    .v-overlay,
    .v-menu,
    .v-tooltip {
      display: none !important;
    }

    /* Nadpisy sekcí (kategorie / čas) */
    .v-card-title {
      display: block !important;
      font-size: 9pt !important;
      font-weight: 700 !important;
      color: #111 !important;
      background: #fff !important;
      border: none !important;
      padding: 2pt 4pt !important;
      margin: 7pt 0 1pt !important;
      page-break-after: avoid;
      break-after: avoid;
    }

    .v-card-subtitle {
      display: block !important;
      font-size: 7pt !important;
      color: #444 !important;
      background: transparent !important;
      border: none !important;
      padding: 0 4pt 2pt !important;
      margin: 0 !important;
      page-break-after: avoid;
      break-after: avoid;
    }

    /* Tabulky */
    table {
      width: 100% !important;
      border-collapse: collapse !important;
      table-layout: auto !important;
      margin: 0 0 4pt !important;
      page-break-inside: auto;
    }

    thead {
      display: table-header-group !important;
    }

    thead tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .print-startlist-time-table .print-time-row td {
      font-weight: 700 !important;
      background: #fff !important;
      border: none !important;
      padding-top: 6pt !important;
      padding-bottom: 1pt !important;
    }

    .print-startlist-time-table .print-row-alt td {
      background: #f0f0f0 !important;
    }

    tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    th, td {
      border: none !important;
      padding: 1.5pt 3pt !important;
      vertical-align: top !important;
      text-align: left !important;
      font-size: 7.5pt !important;
      background: #fff !important;
      color: #111 !important;
    }

    th {
      background: #fff !important;
      color: #111 !important;
      font-weight: 700 !important;
      font-size: 7pt !important;
      border: 1px solid #888 !important;
      white-space: nowrap;
    }

    /* Mezera 3px pod hlavičkou sloupců */
    tbody tr:first-child td {
      padding-top: calc(1.5pt + 3px) !important;
    }

    tbody tr:nth-child(even) td {
      background: #f0f0f0 !important;
    }

    /* --- Startovka: minimum orámování --- */
    .print-startlist th {
      border: 1px solid #888 !important;
      background: #fff !important;
      color: #111 !important;
    }

    .print-startlist td {
      border: none !important;
    }

    .print-startlist .v-card-title {
      background: #fff !important;
      margin-top: 6pt !important;
    }

    /* Stejné šířky sloupců jako u zobrazení podle času (5 stejně širokých) */
    .print-startlist table {
      table-layout: fixed !important;
    }

    .print-startlist th,
    .print-startlist td {
      width: 20% !important;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .print-startlist [style*="border-bottom"] {
      border-bottom: none !important;
      margin-bottom: 2pt !important;
      padding-bottom: 0 !important;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .startlist-header-row {
      display: flex !important;
      font-weight: 700;
      font-size: 7pt;
      background: #fff !important;
      color: #111 !important;
      border: 1px solid #888 !important;
      padding: 2pt 3pt;
      margin: 0 0 2pt;
    }

    .startlist-header-cell {
      flex: 1 1 0;
      width: 20%;
      max-width: 20%;
      padding-right: 4pt;
      overflow: hidden;
    }

    .startlist-item {
      border: none !important;
    }

    .startlist-row {
      display: flex !important;
      padding: 1pt 3pt !important;
      page-break-inside: avoid;
      break-inside: avoid;
      border: none !important;
      background: #fff;
    }

    .startlist-item:nth-child(even) .startlist-row {
      background: #f0f0f0 !important;
    }

    .startlist-cell {
      flex: 1 1 0;
      width: 20%;
      max-width: 20%;
      padding-right: 4pt !important;
      font-size: 7.5pt;
      overflow: hidden;
    }

    /* --- Výsledky --- */
    .print-results {
      font-size: 7pt;
    }

    .print-cat-bar {
      display: flex !important;
      justify-content: space-between;
      align-items: baseline;
      gap: 8pt;
      margin: 6pt 0 2pt !important;
      page-break-after: avoid;
      break-after: avoid;
    }

    .print-cat-name {
      font-size: 8pt !important;
      font-weight: 700 !important;
      color: #111 !important;
      background: #fff !important;
      white-space: nowrap;
      flex: 0 0 auto;
    }

    .print-cat-controls {
      font-size: 6.5pt !important;
      color: #333 !important;
      text-align: right !important;
      flex: 1 1 auto;
      min-width: 0;
    }

    .print-results .v-card-title,
    .print-results .v-card-subtitle {
      display: none !important;
    }

    .print-results table {
      table-layout: auto !important;
    }

    .print-results th,
    .print-results td {
      font-size: 7pt !important;
      padding: 1pt 2pt !important;
      vertical-align: top !important;
    }

    .print-results tbody tr:first-child td {
      padding-top: calc(1pt + 3px) !important;
    }

    .print-results th {
      border: 1px solid #888 !important;
      background: #fff !important;
      color: #111 !important;
    }

    .print-results .print-col-narrow {
      white-space: nowrap !important;
      width: 1% !important;
      max-width: none;
      padding-left: 4pt !important;
      padding-right: 4pt !important;
    }

    .print-results .print-col-name {
      white-space: nowrap !important;
      width: 1% !important;
      padding-left: 4pt !important;
      padding-right: 4pt !important;
    }

    .print-results .print-col-wrap {
      white-space: normal !important;
      word-break: break-word;
      overflow-wrap: anywhere;
    }

    /* Data (ne hlavičky) o 1px větší: Um., Jméno, Index, Čas, Fox */
    .print-results td.print-col-data-lg {
      font-size: calc(7pt + 1px) !important;
    }

    /* Pořadí o 2px menší než běžná data */
    .print-results td.print-col-order {
      font-size: calc(7pt - 1px) !important;
    }

    .print-results .print-col-splits {
      white-space: normal !important;
      width: 42% !important;
    }

    .print-splits {
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: flex-start;
      gap: 0 5pt;
    }

    .print-split {
      display: inline-block !important;
      text-align: right !important;
      font-size: 6pt !important;
      line-height: 1.1 !important;
      margin: 0 0 1pt !important;
      white-space: nowrap !important;
      vertical-align: top;
    }

    .print-split-top {
      font-size: 6pt !important;
      color: #111 !important;
    }

    .print-split-bot {
      font-size: 5.5pt !important;
      color: #111 !important;
      text-align: right !important;
    }

    .print-split.is-warn .print-split-top,
    .print-split.is-warn .print-split-bot {
      color: #b35c00 !important;
    }

    .lost_time {
      color: #666 !important;
      font-size: calc(1em - 4px) !important;
      font-weight: 400 !important;
    }

    .text-muted,
    .text-orange-darken-3 {
      color: #555 !important;
    }

    .text-orange-darken-3 {
      color: #b35c00 !important;
    }

    a, a:visited {
      color: #111 !important;
      text-decoration: none !important;
    }

    strong, b {
      font-weight: 700;
    }

    .text-center:empty,
    .ma-1:empty {
      display: none !important;
    }
  </style>
</head>
<body class="${modeClass}">
  ${headerHtml}
  <main class="print-body">${clone.innerHTML}</main>
</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    console.warn('Nepodařilo se otevřít okno pro tisk (popup blocker?).');
    return;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();

  const doPrint = () => {
    try {
      printWindow.print();
    } finally {
      setTimeout(() => {
        try { printWindow.close(); } catch (_) { /* ignore */ }
      }, 200);
    }
  };

  if (printWindow.document.readyState === 'complete') {
    setTimeout(doPrint, 50);
  } else {
    printWindow.onload = () => setTimeout(doPrint, 50);
  }
}

/**
 * Startovka podle času (flex) → jedna tabulka s <thead>,
 * aby se názvy sloupců opakovaly na každé tištěné stránce.
 */
function convertTimeStartlistToTable(root) {
  const headerRow = root.querySelector('.startlist-header-row');
  if (!headerRow) return;

  const headers = [...headerRow.querySelectorAll('.startlist-header-cell')]
    .map((c) => (c.textContent || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  if (!headers.length) return;

  const timeTitles = [...root.querySelectorAll('.print-time-title')];
  if (!timeTitles.length) return;

  const table = document.createElement('table');
  table.className = 'print-startlist-time-table';

  const thead = document.createElement('thead');
  const thr = document.createElement('tr');
  headers.forEach((h) => {
    const th = document.createElement('th');
    th.textContent = h;
    thr.appendChild(th);
  });
  thead.appendChild(thr);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  let dataIdx = 0;

  timeTitles.forEach((titleEl) => {
    const trTime = document.createElement('tr');
    trTime.className = 'print-time-row';
    const tdTime = document.createElement('td');
    tdTime.colSpan = headers.length;
    tdTime.textContent = (titleEl.textContent || '').replace(/\s+/g, ' ').trim();
    trTime.appendChild(tdTime);
    tbody.appendChild(trTime);

    const section = titleEl.parentElement || titleEl;
    section.querySelectorAll('.startlist-row').forEach((row) => {
      const tr = document.createElement('tr');
      if (dataIdx % 2 === 1) tr.classList.add('print-row-alt');
      dataIdx += 1;
      row.querySelectorAll('.startlist-cell').forEach((cell) => {
        const td = document.createElement('td');
        td.textContent = (cell.textContent || '').replace(/\s+/g, ' ').trim();
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
  });

  table.appendChild(tbody);
  root.innerHTML = '';
  root.appendChild(table);
}

/** Odstraní sloupce tabulky podle textu hlavičky (např. Body / Points). */
function removeTableColumnsByHeader(root, matchWords) {
  const words = matchWords.map((w) => String(w).toLowerCase());
  root.querySelectorAll('table').forEach((table) => {
    const ths = [...table.querySelectorAll('thead tr th')];
    const indexes = [];
    ths.forEach((th, idx) => {
      const text = (th.textContent || '').trim().toLowerCase();
      if (words.some((w) => text === w || text.includes(w))) {
        indexes.push(idx);
      }
    });
    // od nejvyššího indexu, ať se neposunou
    indexes.sort((a, b) => b - a).forEach((idx) => {
      const headerRow = table.querySelector('thead tr');
      if (headerRow && headerRow.children[idx]) {
        headerRow.children[idx].remove();
      }
      table.querySelectorAll('tbody tr').forEach((tr) => {
        if (tr.children[idx]) tr.children[idx].remove();
      });
    });
  });
}

/** Kategorie vlevo, kontroly vpravo na jednom řádku. */
function layoutResultsCategoryBars(root) {
  root.querySelectorAll('.v-card-title').forEach((titleEl) => {
    const parent = titleEl.parentElement;
    if (!parent) return;

    let subtitleEl = parent.querySelector('.v-card-subtitle');
    if (!subtitleEl && titleEl.nextElementSibling?.classList?.contains('v-card-subtitle')) {
      subtitleEl = titleEl.nextElementSibling;
    }
    if (!subtitleEl) {
      subtitleEl = parent.parentElement?.querySelector('.v-card-subtitle') || null;
    }

    const bar = document.createElement('div');
    bar.className = 'print-cat-bar';

    const name = document.createElement('div');
    name.className = 'print-cat-name';
    name.textContent = (titleEl.textContent || '').replace(/\s+/g, ' ').trim();
    bar.appendChild(name);

    if (subtitleEl) {
      const controls = document.createElement('div');
      controls.className = 'print-cat-controls';
      controls.textContent = (subtitleEl.textContent || '').replace(/\s+/g, ' ').trim();
      bar.appendChild(controls);
      subtitleEl.remove();
    }

    titleEl.replaceWith(bar);
  });
}

/** Označí sloupce výsledků: úzké bez zalamování vs. zalamovatelné. */
function markResultsColumns(root) {
  root.querySelectorAll('table').forEach((table) => {
    const ths = [...table.querySelectorAll('thead tr th')];
    ths.forEach((th, idx) => {
      const text = (th.textContent || '').trim().toLowerCase();
      let cls = 'print-col-narrow';
      let dataLg = false;
      if (text.includes('mezičas') || text.includes('split')) {
        cls = 'print-col-wrap print-col-splits';
      } else if (text.includes('pořad') || text.includes('order')) {
        cls = 'print-col-wrap print-col-order';
      } else if (
        text.includes('jméno')
        || text.includes('name')
        || text === 'name'
      ) {
        cls = 'print-col-name';
        dataLg = true;
      } else if (
        text.includes('um')
        || text.includes('place')
        || text.includes('index')
        || text.includes('čas')
        || text.includes('time')
        || text.includes('fox')
      ) {
        dataLg = true;
      }
      // VT a ostatní zůstanou úzké bez zvětšení
      th.classList.add(...cls.split(' '));
      table.querySelectorAll('tbody tr').forEach((tr) => {
        const cell = tr.children[idx];
        if (!cell) return;
        cell.classList.add(...cls.split(' '));
        if (dataLg) cell.classList.add('print-col-data-lg');
      });
    });
  });
}

/**
 * Mezičasy jako na frontendu: u každé kontroly nahoře kód+split,
 * dole cumulační čas; bloky vedle sebe a zalamují se.
 */
function formatResultSplitsStacked(root) {
  root.querySelectorAll('td .d-flex').forEach((flex) => {
    const wrap = document.createElement('div');
    wrap.className = 'print-splits';

    [...flex.querySelectorAll(':scope > div')].forEach((punch) => {
      const lines = [...punch.querySelectorAll(':scope > div')]
        .map((d) => (d.textContent || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean);
      if (!lines.length) return;

      const item = document.createElement('div');
      item.className = 'print-split';
      if (
        punch.classList.contains('text-orange-darken-3')
        || (punch.getAttribute('class') || '').includes('orange')
      ) {
        item.classList.add('is-warn');
      }

      const top = document.createElement('div');
      top.className = 'print-split-top';
      // zachovej tučné kódy z původního HTML, pokud jsou
      const firstLine = punch.querySelector(':scope > div');
      if (firstLine && firstLine.querySelector('b, strong')) {
        top.innerHTML = firstLine.innerHTML;
      } else {
        top.textContent = lines[0];
      }
      item.appendChild(top);

      if (lines[1]) {
        const bot = document.createElement('div');
        bot.className = 'print-split-bot';
        bot.textContent = lines[1];
        item.appendChild(bot);
      }

      wrap.appendChild(item);
    });

    flex.replaceWith(wrap);
  });
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
